import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { requestPasswordResetAsync, resetPasswordAsync } from '../../../features/auth/passwordResetActions';
import { clearPasswordReset } from '../../../features/auth/passwordResetSlice';
import { logout } from '../../../features/auth/authSlice';
import Toast from '../../../components/Toast';

const PasswordRecoveryContext = createContext(null);
export function PasswordRecoveryProvider({ mode, children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { loading, error, message } = useSelector(state => state.passwordReset);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [complete, setComplete] = useState(false);
  const request = useRef(null);
  const active = useRef(false);
  const reset = mode === 'reset';
  const token = new URLSearchParams(location.hash.slice(1)).get('token') || new URLSearchParams(location.search).get('token') || '';
  const validToken = /^[a-f0-9]{64}$/.test(token);
  useEffect(() => {
    active.current = true;
    dispatch(clearPasswordReset());
    return () => { active.current = false; request.current?.abort(); request.current = null; dispatch(clearPasswordReset()); };
  }, [dispatch]);
  const fail = text => { setValidationError(text); Toast('error', text); };
  const handleSubmit = async event => {
    event.preventDefault();
    if (loading || request.current || complete) return;
    setValidationError('');
    if (reset) {
      if (!validToken) return fail('This link is invalid. Please request a new password reset link.');
      if (password.length < 8 || new Blob([password]).size > 72 || !password.trim()) return fail('Use at least 8 characters and no more than 72 bytes for your password.');
      if (password !== confirmPassword) return fail('Your passwords do not match.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return fail('Enter a valid email address.');
    const pending = dispatch(reset ? resetPasswordAsync({ token, password }) : requestPasswordResetAsync({ email: email.trim().toLowerCase() }));
    request.current = pending;
    try {
      const data = await pending.unwrap();
      if (!active.current) return;
      if (reset) { setPassword(''); setConfirmPassword(''); dispatch(logout()); }
      setComplete(true);
      Toast('success', reset ? 'Password reset successfully.' : 'Password reset requested. Please check your email.');
      return data;
    } catch (failure) {
      if (active.current && failure?.name !== 'AbortError') Toast('error', String(failure || 'Unable to complete your request.'));
    } finally { if (request.current === pending) request.current = null; }
  };
  const startAgain = () => { setComplete(false); setValidationError(''); dispatch(clearPasswordReset()); };
  return <PasswordRecoveryContext.Provider value={{ reset, email, setEmail, password, setPassword,
    confirmPassword, setConfirmPassword, loading, error: validationError || error, message,
    complete, validToken, handleSubmit, startAgain }}>{children}</PasswordRecoveryContext.Provider>;
}
export const usePasswordRecovery = () => useContext(PasswordRecoveryContext);
